/**
 * OTA CI/CD Pipeline
 * パイプラインオーケストレーター: SiTL & HiLSテスト（並列実行） → 承認
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Modal } from '../components/ui';
import ApprovalModal from '../components/ApprovalModal';
import ApprovalTimeline, { buildToTimelineEvents } from '../components/ApprovalTimeline';

// パイプラインステージ定義
const PIPELINE_STAGES = [
  { key: 'source', label: 'ソース取得', icon: 'git-branch' },
  { key: 'sitl', label: 'SiTL', icon: 'activity' },
  { key: 'hils', label: 'HiLS', icon: 'cpu' },
  { key: 'artifact_check', label: '成果物検証', icon: 'shield' },
  { key: 'ota_agent', label: 'OTA-agent', icon: 'package' },
  { key: 'ota_test', label: 'OTAテスト', icon: 'upload-cloud' },
  { key: 'approval', label: '承認', icon: 'check-square' },
];

// パイプラインステータス定義
const PIPELINE_STATUS = {
  TESTS_STARTING: { label: 'テスト開始中', color: 'bg-yellow-100 text-yellow-700' },
  TESTS_RUNNING: { label: 'テスト実行中', color: 'bg-blue-100 text-blue-700' },
  TESTS_FAILED: { label: 'テスト失敗', color: 'bg-red-100 text-red-700' },
  SITL_QUEUED: { label: 'SiTLキュー中', color: 'bg-purple-100 text-purple-700' },
  SITL_STARTING: { label: 'SiTL開始中', color: 'bg-yellow-100 text-yellow-700' },
  SITL_RUNNING: { label: 'SiTL実行中', color: 'bg-blue-100 text-blue-700' },
  SITL_FAILED: { label: 'SiTL失敗', color: 'bg-red-100 text-red-700' },
  HILS_QUEUED: { label: 'HiLSキュー中', color: 'bg-purple-100 text-purple-700' },
  HILS_STARTING: { label: 'HiLS開始中', color: 'bg-yellow-100 text-yellow-700' },
  HILS_RUNNING: { label: 'HiLS実行中', color: 'bg-blue-100 text-blue-700' },
  HILS_FAILED: { label: 'HiLS失敗', color: 'bg-red-100 text-red-700' },
  HILS_SKIPPED: { label: 'HiLSスキップ', color: 'bg-slate-100 text-slate-600' },
  OTA_AGENT_BUILDING: { label: 'OTA-agentビルド中', color: 'bg-blue-100 text-blue-700' },
  OTA_TESTING: { label: 'OTAテスト中', color: 'bg-blue-100 text-blue-700' },
  PENDING_APPROVAL: { label: '承認待ち', color: 'bg-yellow-100 text-yellow-700' },
  APPROVED: { label: '承認済み', color: 'bg-green-100 text-green-700' },
  REJECTED: { label: '却下', color: 'bg-red-100 text-red-700' },
  CANCELLED: { label: '中止', color: 'bg-slate-100 text-slate-700' },
};

const APPROVAL_STATUS = {
  PENDING: { label: '承認待ち', color: 'bg-yellow-100 text-yellow-700' },
  APPROVED: { label: '承認済み', color: 'bg-green-100 text-green-700' },
  REJECTED: { label: '却下', color: 'bg-red-100 text-red-700' },
};

// ステージカラー（コンポーネント外に定義してちらつき防止）
const STAGE_COLORS = {
  pending: 'bg-slate-200 text-slate-500',
  in_progress: 'bg-blue-500 text-white',
  completed: 'bg-green-500 text-white',
  failed: 'bg-red-500 text-white',
  skipped: 'bg-slate-300 text-slate-600',
  disabled: 'bg-slate-100 text-slate-400',
  skipped: 'bg-slate-300 text-slate-600',
};

export default function Pipeline() {
  const { pipelineId: urlPipelineId } = useParams();
  const navigate = useNavigate();

  // 状態
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // パイプライン開始モーダル
  const [showStartModal, setShowStartModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [gitClean, setGitClean] = useState(false);
  const [cleanDocker, setCleanDocker] = useState(false);
  const [cleanYoctoCache, setCleanYoctoCache] = useState(false);
  const [skipHilsBuild, setSkipHilsBuild] = useState(false);
  // SiTLはローカルランナー（devpc2）で実行される。デフォルトは実行
  const [skipSitl, setSkipSitl] = useState(false);
  const [starting, setStarting] = useState(false);

  // 実行状態チェック
  const [executionStatus, setExecutionStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // 承認モーダル
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approving, setApproving] = useState(false);

  // OTAテスト再実行
  const [retryingOta, setRetryingOta] = useState(false);

  // タイムライン
  const [timelineEvents, setTimelineEvents] = useState([]);

  // アーティファクトチェック
  const [artifactChecks, setArtifactChecks] = useState([]);
  const [selectedArtifactCheck, setSelectedArtifactCheck] = useState(null);
  const [showArtifactCheckModal, setShowArtifactCheckModal] = useState(false);

  // パイプライン一覧取得（silent: 自動更新時はローディング表示しない）
  const fetchPipelines = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const result = await buildApi.listPipelines({ limit: 10, offset: 0 });
      setPipelines(result.pipelines || []);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
    } catch (err) {
      if (!silent) setError('パイプライン一覧の取得に失敗しました: ' + err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // もっと読み込む
  const loadMorePipelines = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await buildApi.listPipelines({ limit: 10, offset: pipelines.length });
      setPipelines((prev) => [...prev, ...(result.pipelines || [])]);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
    } catch (err) {
      console.error('Failed to load more pipelines:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, pipelines.length]);

  // パイプライン詳細取得
  const fetchPipelineDetail = useCallback(async (pipelineId) => {
    try {
      const detail = await buildApi.getPipeline(pipelineId);
      setSelectedPipeline(detail);
      // タイムラインイベント生成
      const events = pipelineToTimelineEvents(detail);
      setTimelineEvents(events);

      // アーティファクトチェック取得（時刻でフィルターするため全件取得）
      fetchArtifactChecks();
    } catch (err) {
      console.error('Failed to fetch pipeline detail:', err);
    }
  }, []);

  // アーティファクトチェック取得（ブランチ指定なし、時刻でフィルター）
  const fetchArtifactChecks = useCallback(async () => {
    try {
      const result = await buildApi.listArtifactChecks({ limit: 20 });
      setArtifactChecks(result.checks || []);
    } catch (err) {
      console.error('Failed to fetch artifact checks:', err);
      setArtifactChecks([]);
    }
  }, []);

  // ブランチ一覧取得
  const fetchBranches = useCallback(async () => {
    try {
      const result = await buildApi.getGitBranches();
      setBranches(result.branches || ['main']);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
      setBranches(['main', 'develop']);
    }
  }, []);

  // パイプライン実行状態チェック
  const checkExecutionStatus = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const result = await buildApi.getPipelineStatus();
      setExecutionStatus(result);
    } catch (err) {
      console.error('Failed to check execution status:', err);
      setExecutionStatus({ canStart: true, runningTests: [], runningPipelines: [], scheduledTests: [] });
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  // パイプライン開始モーダルを開く
  const handleOpenStartModal = async () => {
    setShowStartModal(true);
    await checkExecutionStatus();
  };

  useEffect(() => {
    fetchPipelines();
    fetchBranches();
  }, [fetchPipelines, fetchBranches]);

  // 前回取得したパイプラインIDを追跡（重複取得防止）
  const lastFetchedPipelineId = useRef(null);

  // URLにパイプラインIDがある場合はそれを表示、ない場合は最新を自動選択
  useEffect(() => {
    if (urlPipelineId) {
      // 同じIDの場合はスキップ（重複取得防止）
      if (lastFetchedPipelineId.current === urlPipelineId) {
        return;
      }
      lastFetchedPipelineId.current = urlPipelineId;
      fetchPipelineDetail(urlPipelineId);
    } else if (pipelines.length > 0) {
      // URLがない場合は最新のパイプラインを自動選択
      const latest = pipelines[0];
      navigate(`/pipeline/${latest.pipelineId}`, { replace: true });
    }
  }, [urlPipelineId, pipelines, fetchPipelineDetail, navigate]);

  // 自動更新（実行中のパイプラインがある場合）
  const runningStates = ['TESTS_STARTING', 'TESTS_RUNNING', 'SITL_QUEUED', 'SITL_STARTING', 'SITL_RUNNING', 'HILS_RUNNING'];
  useEffect(() => {
    const hasInProgress = pipelines.some((p) => runningStates.includes(p.status));
    if (!hasInProgress) return;
    const interval = setInterval(() => {
      fetchPipelines(true); // silent=true でローディング表示なし
      if (selectedPipeline && runningStates.includes(selectedPipeline.status)) {
        fetchPipelineDetail(selectedPipeline.pipelineId);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [pipelines, selectedPipeline, fetchPipelines, fetchPipelineDetail]);

  // パイプライン選択（navigateのみ、データ取得はuseEffectで行う）
  const handleSelectPipeline = (pipeline) => {
    // 同じパイプラインを再選択した場合は何もしない
    if (selectedPipeline?.pipelineId === pipeline.pipelineId) {
      return;
    }
    navigate(`/pipeline/${pipeline.pipelineId}`);
  };

  // パイプライン開始
  const handleStartPipeline = async () => {
    setStarting(true);
    try {
      // 開始前に再度実行状態をチェック
      const statusCheck = await buildApi.getPipelineStatus();
      if (!statusCheck.canStart) {
        alert(`パイプラインを開始できません: ${statusCheck.message}`);
        setExecutionStatus(statusCheck);
        setStarting(false);
        return;
      }

      const result = await buildApi.createPipeline({
        branch: selectedBranch,
        gitClean,
        cleanDocker,
        cleanYoctoCache,
        skipHilsBuild,
        skipSitl,
      });
      setShowStartModal(false);
      await fetchPipelines();
      // 新しいパイプラインを選択
      navigate(`/pipeline/${result.pipelineId}`);
      fetchPipelineDetail(result.pipelineId);
    } catch (err) {
      alert('パイプライン開始エラー: ' + err.message);
    } finally {
      setStarting(false);
    }
  };

  // パイプライン中止
  const [stopping, setStopping] = useState(false);

  const handleStopPipeline = async (pipelineId) => {
    if (!window.confirm('実行中のパイプラインを中止しますか？')) {
      return;
    }
    setStopping(true);
    try {
      await buildApi.stopPipeline(pipelineId);
      await fetchPipelines();
      if (selectedPipeline?.pipelineId === pipelineId) {
        await fetchPipelineDetail(pipelineId);
      }
    } catch (err) {
      alert('パイプライン中止エラー: ' + err.message);
    } finally {
      setStopping(false);
    }
  };

  // 承認処理
  const handleApprove = async (comment, releaseVersion) => {
    setApproving(true);
    try {
      const result = await buildApi.approvePipeline(selectedPipeline.pipelineId, true, comment, releaseVersion);
      setShowApprovalModal(false);
      if (result?.message) {
        alert(result.message);
      }
      await fetchPipelineDetail(selectedPipeline.pipelineId);
      await fetchPipelines();
    } catch (err) {
      alert('承認処理エラー: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  // 却下処理
  const handleReject = async (comment) => {
    setApproving(true);
    try {
      await buildApi.approvePipeline(selectedPipeline.pipelineId, false, comment);
      setShowApprovalModal(false);
      await fetchPipelineDetail(selectedPipeline.pipelineId);
      await fetchPipelines();
    } catch (err) {
      alert('却下処理エラー: ' + err.message);
    } finally {
      setApproving(false);
    }
  };

  // OTAテスト再実行
  const handleRetryOtaTest = async () => {
    if (!selectedPipeline) return;
    if (!confirm('OTAテストを再実行しますか？デバイスがオンラインであることを確認してください。')) return;

    setRetryingOta(true);
    try {
      const result = await buildApi.retryPipelineOtaTest(selectedPipeline.pipelineId);
      alert(`OTAテストを再実行しました\nジョブID: ${result.jobId}\nバージョン: v${result.version}\nオンラインデバイス数: ${result.onlineDevices}`);
      await fetchPipelineDetail(selectedPipeline.pipelineId);
      await fetchPipelines();
    } catch (err) {
      alert('OTAテスト再実行エラー: ' + err.message);
    } finally {
      setRetryingOta(false);
    }
  };

  // ユーティリティ
  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  // パイプラインからタイムラインイベントを生成
  const pipelineToTimelineEvents = (pipeline) => {
    if (!pipeline) return [];
    const events = [];

    if (pipeline.createdAt) {
      events.push({
        type: 'start',
        label: 'パイプライン開始',
        timestamp: pipeline.createdAt,
        user: pipeline.createdByEmail || pipeline.createdBy,
      });
    }

    if (pipeline.sitl?.startedAt) {
      events.push({
        type: 'test',
        label: 'SiTLテスト開始',
        timestamp: pipeline.sitl.startedAt,
        status: pipeline.sitl.status,
      });
    }

    if (pipeline.sitl?.completedAt) {
      events.push({
        type: pipeline.sitl.exitCode === 0 ? 'success' : 'error',
        label: pipeline.sitl.exitCode === 0 ? 'SiTLテスト完了' : 'SiTLテスト失敗',
        timestamp: pipeline.sitl.completedAt,
      });
    }

    if (pipeline.approval?.approvedAt) {
      events.push({
        type: pipeline.approval.status === 'APPROVED' ? 'approval' : 'rejection',
        label: pipeline.approval.status === 'APPROVED' ? '承認' : '却下',
        timestamp: pipeline.approval.approvedAt,
        user: pipeline.approval.approvedByEmail || pipeline.approval.approvedBy,
        comment: pipeline.approval.comment,
      });
    }

    return events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  // パイプラインに紐づくアーティファクトチェックのみをフィルター
  // HiLSのGitHub Run IDで照合（最も正確）、なければ時刻でフィルター
  const filteredArtifactChecks = useMemo(() => {
    if (!selectedPipeline?.createdAt || artifactChecks.length === 0) return [];

    const hilsRunId = selectedPipeline.hils?.githubRunId;
    if (hilsRunId) {
      // HiLSのRun IDと一致する成果物検証のみを表示
      return artifactChecks.filter(check => {
        return check.sha === String(hilsRunId);
      });
    }

    // Run IDがない場合はパイプライン開始時刻でフィルター（フォールバック）
    const pipelineStartTime = new Date(selectedPipeline.createdAt).getTime();
    return artifactChecks.filter(check => {
      const checkTime = new Date(check.startTime).getTime();
      return checkTime >= pipelineStartTime;
    });
  }, [selectedPipeline?.createdAt, selectedPipeline?.hils?.githubRunId, artifactChecks]);

  // パイプラインステージ状態取得
  const getStageStatus = (pipeline, stageKey) => {
    if (!pipeline) return 'pending';

    // SiTL・HiLS並列実行に対応
    const sitlStatus = pipeline.sitl?.status;
    const hilsStatus = pipeline.hils?.status;

    switch (stageKey) {
      case 'source':
        // ソース取得は常に完了（パイプライン開始時に実行）
        return 'completed';

      case 'sitl':
        if (['QUEUED', 'STARTING', 'STARTING_EC2', 'RUNNING'].includes(sitlStatus)) return 'in_progress';
        if (sitlStatus === 'COMPLETED' && pipeline.sitl?.exitCode === 0) return 'completed';
        if (sitlStatus === 'COMPLETED' && pipeline.sitl?.exitCode !== 0) return 'failed';
        if (sitlStatus === 'SKIPPED') return 'skipped';
        if (sitlStatus === 'FAILED' || sitlStatus === 'CANCELLED') return 'failed';
        return 'pending';

      case 'hils':
        if (['QUEUED', 'STARTING', 'RUNNING'].includes(hilsStatus)) return 'in_progress';
        if (hilsStatus === 'COMPLETED') return 'completed';
        if (hilsStatus === 'SKIPPED') return 'skipped';
        if (hilsStatus === 'FAILED' || hilsStatus === 'CANCELLED') return 'failed';
        return 'pending';

      case 'artifact_check':
        // HiLSが完了またはスキップするまではpending
        if (hilsStatus !== 'COMPLETED' && hilsStatus !== 'FAILED' && hilsStatus !== 'SKIPPED') return 'pending';
        // HiLSがスキップされた場合、成果物検証もスキップ
        if (hilsStatus === 'SKIPPED') return 'skipped';
        // アーティファクトチェックの状態を判定
        if (filteredArtifactChecks.length === 0) return 'pending';
        const latestCheck = filteredArtifactChecks[0];
        if (latestCheck.status === 'PASSED') return 'completed';
        if (latestCheck.status === 'FAILED') return 'failed';
        return 'in_progress';

      case 'ota_agent':
        // SiTLが完了またはスキップ、かつHiLSが完了またはスキップするまではpending
        if (sitlStatus !== 'COMPLETED' && sitlStatus !== 'FAILED' && sitlStatus !== 'SKIPPED') return 'pending';
        if (hilsStatus !== 'COMPLETED' && hilsStatus !== 'FAILED' && hilsStatus !== 'SKIPPED') return 'pending';
        // ota-agentビルドの状態（Yocto統合後は'SKIPPED'になる）
        const otaAgentStatus = pipeline.otaAgentBuild?.status;
        if (otaAgentStatus === 'IN_PROGRESS') return 'in_progress';
        if (otaAgentStatus === 'COMPLETED') return 'completed';
        if (otaAgentStatus === 'SKIPPED') return 'skipped';
        if (otaAgentStatus === 'FAILED') return 'failed';
        return 'pending';

      case 'ota_test':
        // ota-agentビルド完了またはスキップまでpending
        const otaBuildStatus = pipeline.otaAgentBuild?.status;
        if (otaBuildStatus !== 'COMPLETED' && otaBuildStatus !== 'SKIPPED') return 'pending';
        // OTAテストの状態
        const otaTestStatus = pipeline.ota?.status;
        if (otaTestStatus === 'IN_PROGRESS') return 'in_progress';
        if (otaTestStatus === 'COMPLETED') return 'completed';
        if (otaTestStatus === 'FAILED') return 'failed';
        if (otaTestStatus === 'TRIGGER_FAILED') return 'failed';
        return 'pending';

      case 'approval':
        if (pipeline.status === 'PENDING_APPROVAL') return 'in_progress';
        if (pipeline.status === 'APPROVED') return 'completed';
        if (pipeline.status === 'REJECTED') return 'failed';
        return 'pending';

      default:
        return 'pending';
    }
  };

  // ステータスバッジコンポーネント
  const StatusBadge = ({ status, label, icon, onClick, clickable }) => (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${STAGE_COLORS[status]} ${clickable ? 'cursor-pointer hover:opacity-80 hover:scale-105 transition-all' : ''}`}
      onClick={clickable ? onClick : undefined}
      title={clickable ? 'クリックで詳細を表示' : undefined}
    >
      {status === 'in_progress' && (
        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {status === 'completed' && <Icon name="check" className="w-3 h-3" />}
      {status === 'failed' && <Icon name="x" className="w-3 h-3" />}
      {status === 'skipped' && <Icon name="skip-forward" className="w-3 h-3" />}
      <Icon name={icon} className="w-3 h-3" />
      {label}
    </div>
  );

  // パイプラインステータス概要（カード形式）
  const pipelineStatusCards = useMemo(() => {
    if (!selectedPipeline) return null;

    const sitlStatus = getStageStatus(selectedPipeline, 'sitl');
    const hilsStatus = getStageStatus(selectedPipeline, 'hils');
    const artifactCheckStatus = getStageStatus(selectedPipeline, 'artifact_check');
    const approvalStatus = getStageStatus(selectedPipeline, 'approval');

    return (
      <div className="space-y-3">
        {/* 並列テスト */}
        <div className="grid grid-cols-2 gap-3">
          {/* SiTL */}
          <div
            className={`p-3 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${
              sitlStatus === 'completed' ? 'border-green-300 bg-green-50' :
              sitlStatus === 'skipped' ? 'border-slate-300 bg-slate-100' :
              sitlStatus === 'failed' ? 'border-red-300 bg-red-50' :
              sitlStatus === 'in_progress' ? 'border-blue-300 bg-blue-50' :
              'border-slate-200 bg-slate-50'
            }`}
            onClick={() => {
              const testId = selectedPipeline.sitl?.testId;
              navigate(testId ? `/sitl-tests?testId=${encodeURIComponent(testId)}` : '/sitl-tests');
            }}
            title="SiTLテストページへ移動（紐づくテストを表示）"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="activity" className="w-4 h-4" />
                <span className="font-medium">SiTL</span>
              </div>
              <StatusBadge status={sitlStatus} label={sitlStatus === 'in_progress' ? '実行中' : sitlStatus === 'completed' ? '完了' : sitlStatus === 'skipped' ? 'スキップ' : sitlStatus === 'failed' ? '失敗' : '待機'} icon="activity" />
            </div>
            {selectedPipeline.sitl?.testSummary && (
              <div className="mt-2 text-xs text-slate-600">
                {selectedPipeline.sitl.testSummary.passed}/{selectedPipeline.sitl.testSummary.total} テスト合格
              </div>
            )}
            {sitlStatus === 'skipped' && (
              <div className="mt-2 text-xs text-slate-500">
                SiTLテストをスキップ（GPU問題対応中）
              </div>
            )}
          </div>

          {/* HiLS */}
          <div
            className={`p-3 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${
              hilsStatus === 'completed' ? 'border-green-300 bg-green-50' :
              hilsStatus === 'skipped' ? 'border-slate-300 bg-slate-100' :
              hilsStatus === 'failed' ? 'border-red-300 bg-red-50' :
              hilsStatus === 'in_progress' ? 'border-blue-300 bg-blue-50' :
              'border-slate-200 bg-slate-50'
            }`}
            onClick={() => {
              const runId = selectedPipeline.hils?.githubRunId;
              navigate(runId ? `/hils-tests?runId=${encodeURIComponent(runId)}` : '/hils-tests');
            }}
            title="HiLSテストページへ移動（紐づくテストを表示）"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="cpu" className="w-4 h-4" />
                <span className="font-medium">HiLS</span>
              </div>
              <StatusBadge
                status={hilsStatus}
                label={
                  hilsStatus === 'in_progress' ? '実行中' :
                  hilsStatus === 'completed' ? '完了' :
                  hilsStatus === 'skipped' ? 'スキップ' :
                  hilsStatus === 'failed' ? '失敗' : '待機'
                }
                icon="cpu"
              />
            </div>
            {selectedPipeline.hils?.htmlUrl && (
              <div className="mt-2 text-xs text-slate-600 truncate">
                GitHub Actions
              </div>
            )}
            {hilsStatus === 'skipped' && (
              <div className="mt-2 text-xs text-slate-500">
                HiLSビルドをスキップ
              </div>
            )}
          </div>
        </div>

        {/* 成果物検証 */}
        <div
          className={`p-3 rounded-lg border-2 ${
            getStageStatus(selectedPipeline, 'artifact_check') === 'completed' ? 'border-green-300 bg-green-50 cursor-pointer hover:shadow-md' :
            getStageStatus(selectedPipeline, 'artifact_check') === 'failed' ? 'border-red-300 bg-red-50 cursor-pointer hover:shadow-md' :
            getStageStatus(selectedPipeline, 'artifact_check') === 'skipped' ? 'border-slate-300 bg-slate-100' :
            'border-slate-200 bg-slate-50'
          } transition-all`}
          onClick={filteredArtifactChecks.length > 0 ? () => {
            setSelectedArtifactCheck(filteredArtifactChecks[0]);
            setShowArtifactCheckModal(true);
          } : undefined}
          title={filteredArtifactChecks.length > 0 ? 'クリックで検証詳細を表示' : undefined}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="shield" className="w-4 h-4" />
              <span className="font-medium">成果物検証</span>
            </div>
            <StatusBadge
              status={getStageStatus(selectedPipeline, 'artifact_check')}
              label={
                getStageStatus(selectedPipeline, 'artifact_check') === 'completed' ? 'PASSED' :
                getStageStatus(selectedPipeline, 'artifact_check') === 'failed' ? 'FAILED' :
                getStageStatus(selectedPipeline, 'artifact_check') === 'skipped' ? 'スキップ' : '待機'
              }
              icon="shield"
            />
          </div>
          {getStageStatus(selectedPipeline, 'artifact_check') === 'skipped' && (
            <div className="mt-2 text-xs text-slate-500">
              HiLSスキップのため検証なし
            </div>
          )}
          {filteredArtifactChecks.length > 0 && (
            <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
              <span className="flex items-center gap-1">
                <Icon name="check-circle" className="w-3 h-3 text-green-500" />
                {filteredArtifactChecks[0].checks?.filter(c => c.status === 'PASSED').length || 0} 合格
              </span>
              {filteredArtifactChecks[0].checks?.filter(c => c.status === 'SKIPPED').length > 0 && (
                <span className="flex items-center gap-1">
                  <Icon name="minus-circle" className="w-3 h-3 text-slate-400" />
                  {filteredArtifactChecks[0].checks.filter(c => c.status === 'SKIPPED').length} スキップ
                </span>
              )}
            </div>
          )}
        </div>

        {/* OTA-agent & OTAテスト & 承認 */}
        <div className="flex items-center gap-3">
          {/* OTA-agent ビルド */}
          <div
            className={`flex-1 p-3 rounded-lg border-2 ${
              getStageStatus(selectedPipeline, 'ota_agent') === 'completed' ? 'border-green-300 bg-green-50' :
              getStageStatus(selectedPipeline, 'ota_agent') === 'failed' ? 'border-red-300 bg-red-50' :
              getStageStatus(selectedPipeline, 'ota_agent') === 'in_progress' ? 'border-blue-300 bg-blue-50' :
              'border-slate-200 bg-slate-50'
            } transition-all`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="package" className="w-4 h-4" />
                <span className="font-medium">OTA-agent</span>
              </div>
              <StatusBadge
                status={getStageStatus(selectedPipeline, 'ota_agent')}
                label={
                  getStageStatus(selectedPipeline, 'ota_agent') === 'in_progress' ? 'ビルド中' :
                  getStageStatus(selectedPipeline, 'ota_agent') === 'completed' ? '完了' :
                  getStageStatus(selectedPipeline, 'ota_agent') === 'failed' ? '失敗' : '待機'
                }
                icon="package"
              />
            </div>
            {selectedPipeline.otaAgentBuild?.version && (
              <div className="mt-2 text-xs text-slate-600">
                v{selectedPipeline.otaAgentBuild.version}
              </div>
            )}
          </div>

          {/* OTAテスト */}
          <div
            className={`flex-1 p-3 rounded-lg border-2 ${
              getStageStatus(selectedPipeline, 'ota_test') === 'completed' ? 'border-green-300 bg-green-50' :
              getStageStatus(selectedPipeline, 'ota_test') === 'failed' ? 'border-red-300 bg-red-50' :
              getStageStatus(selectedPipeline, 'ota_test') === 'in_progress' ? 'border-blue-300 bg-blue-50' :
              'border-slate-200 bg-slate-50'
            } transition-all`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="upload-cloud" className="w-4 h-4" />
                <span className="font-medium">OTAテスト</span>
              </div>
              <StatusBadge
                status={getStageStatus(selectedPipeline, 'ota_test')}
                label={
                  getStageStatus(selectedPipeline, 'ota_test') === 'in_progress' ? '配信中' :
                  getStageStatus(selectedPipeline, 'ota_test') === 'completed' ? '成功' :
                  getStageStatus(selectedPipeline, 'ota_test') === 'failed' ? '失敗' : '待機'
                }
                icon="upload-cloud"
              />
            </div>
            {selectedPipeline.ota?.version && (
              <div className="mt-2 text-xs text-slate-600">
                v{selectedPipeline.ota.version}
                {selectedPipeline.ota?.succeeded > 0 && ` (${selectedPipeline.ota.succeeded}台成功)`}
              </div>
            )}
          </div>

          {/* 承認 */}
          <div
            className={`flex-1 p-3 rounded-lg border-2 ${
              approvalStatus === 'completed' ? 'border-green-300 bg-green-50' :
              approvalStatus === 'in_progress' ? 'border-yellow-300 bg-yellow-50' :
              approvalStatus === 'failed' ? 'border-red-300 bg-red-50' :
              'border-slate-200 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="check-square" className="w-4 h-4" />
                <span className="font-medium">承認</span>
              </div>
              <StatusBadge
                status={approvalStatus}
                label={approvalStatus === 'completed' ? '承認済み' : approvalStatus === 'in_progress' ? '承認待ち' : approvalStatus === 'failed' ? '却下' : '待機'}
                icon="check-square"
              />
            </div>
            {selectedPipeline.approval?.approvedByEmail && (
              <div className="mt-2 text-xs text-slate-600 truncate">
                {selectedPipeline.approval.approvedByEmail}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [selectedPipeline?.pipelineId, selectedPipeline?.status, selectedPipeline?.sitl?.status, selectedPipeline?.hils?.status, selectedPipeline?.approval?.status, filteredArtifactChecks, navigate]);

  // 実行中パイプラインをフィルタリング
  const runningPipelinesFiltered = pipelines.filter((p) => runningStates.includes(p.status));
  const hasRunningPipeline = runningPipelinesFiltered.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">OTA CI/CD pipeline</h1>
        </div>
        {hasRunningPipeline ? (
          <Button
            variant="danger"
            onClick={() => handleStopPipeline(runningPipelinesFiltered[0].pipelineId)}
            disabled={stopping}
          >
            {stopping ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                中止中...
              </>
            ) : (
              <>
                <Icon name="square" className="w-4 h-4" />
                パイプライン中止
              </>
            )}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleOpenStartModal}>
            <Icon name="play" className="w-4 h-4" />
            パイプライン開始
          </Button>
        )}
      </div>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左カラム: パイプライン一覧 */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">パイプライン一覧</h3>
              <Button size="sm" variant="ghost" onClick={fetchPipelines}>
                <Icon name="refresh" className="w-4 h-4" />
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : pipelines.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Icon name="inbox" className="w-8 h-8 mx-auto mb-2" />
                <p>パイプラインがありません</p>
                <p className="text-xs mt-2">「パイプライン開始」ボタンで新規作成</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {pipelines.map((pipeline) => {
                  const isSelected = selectedPipeline?.pipelineId === pipeline.pipelineId;
                  const status = PIPELINE_STATUS[pipeline.status] || { label: pipeline.status, color: 'bg-slate-100' };

                  return (
                    <div
                      key={pipeline.pipelineId}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary-50 border-2 border-primary-500'
                          : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                      }`}
                      onClick={() => handleSelectPipeline(pipeline)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-500">
                            {pipeline.pipelineId.slice(-16)}
                          </span>
                          <span className="px-1.5 py-0.5 bg-slate-200 rounded text-xs font-mono">{pipeline.branch}</span>
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(pipeline.createdAt)}</span>
                      </div>
                      {/* 結果バッジ */}
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${status.color}`}>
                          {status.label}
                        </span>
                        {pipeline.sitl?.testSummary && (
                          <span className="text-[10px] text-slate-500">
                            {pipeline.sitl.testSummary.passed}/{pipeline.sitl.testSummary.total} passed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* もっと見るボタン */}
                {hasMore && (
                  <div className="pt-2 text-center">
                    <button
                      onClick={loadMorePipelines}
                      disabled={loadingMore}
                      className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          読み込み中...
                        </span>
                      ) : (
                        `もっと見る（残り${totalCount - pipelines.length}件）`
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* データライフサイクルポリシー */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold mb-1">データ保存ポリシー（準備中）</p>
                <p>テストデータ・ビルド成果物は <span className="font-mono font-semibold">30日</span> 後に自動削除されます。</p>
              </div>
            </div>
          </div>
        </div>

        {/* 右カラム: パイプライン詳細 */}
        <div className="lg:col-span-2 space-y-4">
          {selectedPipeline ? (
            <>
              {/* ヘッダー */}
              <Card>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">
                      {selectedPipeline.pipelineId}
                    </h3>
                    <p className="text-sm text-slate-500">
                      ブランチ: {selectedPipeline.branch}
                      {selectedPipeline.sitl?.commitId && (
                        <span className="font-mono ml-2">
                          ({selectedPipeline.sitl.commitId.slice(0, 8)})
                        </span>
                      )}
                    </p>
                    {selectedPipeline.sitl?.commitMessage && (
                      <p className="text-sm text-slate-600 mt-1">{selectedPipeline.sitl.commitMessage}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${PIPELINE_STATUS[selectedPipeline.status]?.color || 'bg-slate-100'}`}>
                    {PIPELINE_STATUS[selectedPipeline.status]?.label || selectedPipeline.status}
                  </span>
                </div>

                {/* パイプラインステータス（カード形式） */}
                {pipelineStatusCards}
              </Card>

              {/* 成果物検証履歴パネル（複数ある場合のみ表示） */}
              {filteredArtifactChecks.length > 1 && (
                <Card>
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Icon name="shield" className="w-4 h-4" /> 成果物検証履歴
                  </h4>

                  <div className="space-y-2">
                    {filteredArtifactChecks.slice(1, 4).map((check) => (
                      <div
                        key={check.checkId}
                        className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => {
                          setSelectedArtifactCheck(check);
                          setShowArtifactCheckModal(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              check.status === 'PASSED' ? 'bg-green-100 text-green-700' :
                              check.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {check.status}
                            </span>
                            <span className="text-sm font-medium text-slate-700">
                              {check.filename}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(check.startTime).toLocaleString('ja-JP')}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Icon name="file" className="w-3 h-3" />
                            {check.metadata?.sizeFormatted || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="git-branch" className="w-3 h-3" />
                            {check.branch || '-'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="check-circle" className="w-3 h-3 text-green-500" />
                            {check.checks?.filter(c => c.status === 'PASSED').length || 0}
                          </span>
                          {check.checks?.filter(c => c.status === 'SKIPPED').length > 0 && (
                            <span
                              className="flex items-center gap-1 cursor-help"
                              title={check.checks
                                .filter(c => c.status === 'SKIPPED')
                                .map(c => `${c.name}: ${c.details?.message || 'スキップ'}`)
                                .join('\n')}
                            >
                              <Icon name="minus-circle" className="w-3 h-3 text-slate-400" />
                              {check.checks.filter(c => c.status === 'SKIPPED').length} スキップ
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {filteredArtifactChecks.length === 0 && (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      <Icon name="inbox" className="w-6 h-6 mx-auto mb-1" />
                      検証結果がありません
                    </div>
                  )}
                </Card>
              )}

              {/* 詳細パネル */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* SiTLテスト結果 */}
                <Card>
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Icon name="activity" className="w-4 h-4" /> SiTLテスト
                  </h4>

                  <div className="p-3 bg-slate-50 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">テストステータス</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        selectedPipeline.sitl?.status === 'COMPLETED' && selectedPipeline.sitl?.exitCode === 0 ? 'bg-green-100 text-green-700' :
                        selectedPipeline.sitl?.status === 'COMPLETED' && selectedPipeline.sitl?.exitCode !== 0 ? 'bg-red-100 text-red-700' :
                        selectedPipeline.sitl?.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                        selectedPipeline.sitl?.status === 'QUEUED' ? 'bg-purple-100 text-purple-700' :
                        selectedPipeline.sitl?.status === 'STARTING_EC2' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {selectedPipeline.sitl?.status || '未開始'}
                      </span>
                    </div>

                    {/* テストID */}
                    {selectedPipeline.sitl?.testId && (
                      <p className="text-xs text-slate-500 font-mono mb-2">
                        ID: {selectedPipeline.sitl.testId}
                      </p>
                    )}

                    {/* テスト結果サマリー */}
                    {selectedPipeline.sitl?.testSummary && (
                      <div className="text-xs text-slate-600 mb-2">
                        <span className="text-green-600 font-semibold">
                          {selectedPipeline.sitl.testSummary.passed}
                        </span>
                        /{selectedPipeline.sitl.testSummary.total} テスト合格
                        {selectedPipeline.sitl.testSummary.failed > 0 && (
                          <span className="text-red-600 ml-2">
                            ({selectedPipeline.sitl.testSummary.failed} 失敗)
                          </span>
                        )}
                      </div>
                    )}

                    {/* 実行中のインジケーター */}
                    {['QUEUED', 'STARTING_EC2', 'RUNNING'].includes(selectedPipeline.sitl?.status) && (
                      <div className="flex items-center gap-2 text-xs text-blue-600 mt-2">
                        <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        {selectedPipeline.sitl?.status === 'QUEUED' && 'テスト実行待ち...'}
                        {selectedPipeline.sitl?.status === 'STARTING_EC2' && 'EC2インスタンス起動中...'}
                        {selectedPipeline.sitl?.status === 'RUNNING' && 'テスト実行中...'}
                      </div>
                    )}

                    {/* 完了時のメッセージ */}
                    {selectedPipeline.sitl?.status === 'COMPLETED' && selectedPipeline.sitl?.exitCode === 0 && (
                      <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                        <Icon name="check-circle" className="w-4 h-4" />
                        テスト完了（成功）
                      </div>
                    )}
                    {selectedPipeline.sitl?.status === 'COMPLETED' && selectedPipeline.sitl?.exitCode !== 0 && (
                      <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                        <Icon name="x-circle" className="w-4 h-4" />
                        テスト完了（失敗: exitCode={selectedPipeline.sitl.exitCode}）
                      </div>
                    )}
                  </div>

                </Card>

                {/* HiLSテスト結果 */}
                <Card>
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Icon name="cpu" className="w-4 h-4" /> HiLSテスト
                  </h4>

                  <div className="p-3 bg-slate-50 rounded-lg mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">テストステータス</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        selectedPipeline.hils?.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        selectedPipeline.hils?.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                        selectedPipeline.hils?.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                        selectedPipeline.hils?.status === 'QUEUED' ? 'bg-purple-100 text-purple-700' :
                        selectedPipeline.hils?.status === 'STARTING' ? 'bg-yellow-100 text-yellow-700' :
                        selectedPipeline.hils?.status === 'CANCELLED' ? 'bg-slate-100 text-slate-500' :
                        selectedPipeline.hils?.status === 'SKIPPED' ? 'bg-slate-200 text-slate-600' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {selectedPipeline.hils?.status === 'SKIPPED' ? 'スキップ' : (selectedPipeline.hils?.status || '未開始')}
                      </span>
                    </div>

                    {/* GitHub Run ID */}
                    {selectedPipeline.hils?.githubRunId && (
                      <p className="text-xs text-slate-500 font-mono mb-2">
                        Run ID: {selectedPipeline.hils.githubRunId}
                      </p>
                    )}

                    {/* コミット情報 */}
                    {selectedPipeline.hils?.commitSha && (
                      <p className="text-xs text-slate-500 mb-2">
                        <span className="font-mono">{selectedPipeline.hils.commitSha?.slice(0, 8)}</span>
                        {selectedPipeline.hils.commitMessage && (
                          <span className="ml-2">{selectedPipeline.hils.commitMessage.split('\n')[0]}</span>
                        )}
                      </p>
                    )}

                    {/* 実行中の場合 */}
                    {['QUEUED', 'STARTING', 'RUNNING'].includes(selectedPipeline.hils?.status) && (
                      <div className="flex items-center gap-2 text-xs text-blue-600 mt-2">
                        <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        {selectedPipeline.hils?.status === 'QUEUED' && 'ビルド待機中...'}
                        {selectedPipeline.hils?.status === 'STARTING' && 'ビルド開始中...'}
                        {selectedPipeline.hils?.status === 'RUNNING' && 'ビルド実行中...'}
                      </div>
                    )}

                    {/* 成功の場合 */}
                    {selectedPipeline.hils?.status === 'COMPLETED' && (
                      <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                        <Icon name="check-circle" className="w-4 h-4" />
                        ビルド成功
                      </div>
                    )}

                    {/* 失敗の場合 */}
                    {selectedPipeline.hils?.status === 'FAILED' && (
                      <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                        <Icon name="x-circle" className="w-4 h-4" />
                        ビルド失敗 {selectedPipeline.hils?.conclusion && `(${selectedPipeline.hils.conclusion})`}
                      </div>
                    )}

                    {/* スキップの場合 */}
                    {selectedPipeline.hils?.status === 'SKIPPED' && (
                      <div className="flex items-center gap-2 text-xs text-slate-600 mt-2">
                        <Icon name="skip-forward" className="w-4 h-4" />
                        HiLSビルドをスキップ（ユーザー指定）
                      </div>
                    )}

                    {/* GitHubリンク */}
                    {selectedPipeline.hils?.htmlUrl && (
                      <a
                        href={selectedPipeline.hils.htmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2"
                      >
                        <Icon name="external-link" className="w-3 h-3" />
                        GitHubで確認
                      </a>
                    )}
                  </div>
                </Card>

                {/* OTA-agentビルド（表示条件: otaAgentBuildが存在する場合） */}
                {selectedPipeline.otaAgentBuild && (
                  <Card>
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Icon name="package" className="w-4 h-4" /> OTA-agentビルド
                    </h4>

                    <div className="p-3 bg-slate-50 rounded-lg mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">ビルドステータス</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          selectedPipeline.otaAgentBuild?.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          selectedPipeline.otaAgentBuild?.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                          selectedPipeline.otaAgentBuild?.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {selectedPipeline.otaAgentBuild?.status || '待機中'}
                        </span>
                      </div>

                      {selectedPipeline.otaAgentBuild?.version && (
                        <p className="text-xs text-slate-500 mb-2">
                          <span className="text-slate-400">バージョン:</span> v{selectedPipeline.otaAgentBuild.version}
                        </p>
                      )}

                      {selectedPipeline.otaAgentBuild?.status === 'IN_PROGRESS' && (
                        <div className="flex items-center gap-2 text-xs text-blue-600 mt-2">
                          <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          ビルド実行中...
                        </div>
                      )}

                      {selectedPipeline.otaAgentBuild?.status === 'COMPLETED' && (
                        <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                          <Icon name="check-circle" className="w-4 h-4" />
                          ビルド成功
                        </div>
                      )}

                      {selectedPipeline.otaAgentBuild?.status === 'FAILED' && (
                        <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                          <Icon name="x-circle" className="w-4 h-4" />
                          ビルド失敗
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* OTAテスト（表示条件: otaが存在する場合） */}
                {selectedPipeline.ota && (
                  <Card>
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Icon name="upload-cloud" className="w-4 h-4" /> OTAテスト
                    </h4>

                    <div className="p-3 bg-slate-50 rounded-lg mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">テストステータス</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          selectedPipeline.ota?.timedOut ? 'bg-red-100 text-red-700' :
                          selectedPipeline.ota?.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          selectedPipeline.ota?.status === 'FAILED' || selectedPipeline.ota?.status === 'TRIGGER_FAILED' ? 'bg-red-100 text-red-700' :
                          selectedPipeline.ota?.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {selectedPipeline.ota?.timedOut ? 'タイムアウト' :
                           selectedPipeline.ota?.status === 'TRIGGER_FAILED' ? 'トリガー失敗' :
                           selectedPipeline.ota?.status || '待機中'}
                        </span>
                      </div>

                      {selectedPipeline.ota?.version && (
                        <p className="text-xs text-slate-500 mb-2">
                          <span className="text-slate-400">配信バージョン:</span> v{selectedPipeline.ota.version}
                        </p>
                      )}

                      {(selectedPipeline.ota?.succeeded > 0 || selectedPipeline.ota?.failed > 0) && (
                        <p className="text-xs text-slate-500 mb-2">
                          <span className="text-slate-400">結果:</span> 成功 {selectedPipeline.ota.succeeded} / 失敗 {selectedPipeline.ota.failed}
                        </p>
                      )}

                      {selectedPipeline.ota?.status === 'IN_PROGRESS' && !selectedPipeline.ota?.timedOut && (
                        <div className="flex items-center gap-2 text-xs text-blue-600 mt-2">
                          <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          OTA配信中...
                        </div>
                      )}

                      {selectedPipeline.ota?.timedOut && (
                        <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                          <Icon name="clock" className="w-4 h-4" />
                          OTAテストがタイムアウトしました（60分）
                        </div>
                      )}

                      {selectedPipeline.ota?.status === 'COMPLETED' && selectedPipeline.ota?.succeeded > 0 && selectedPipeline.ota?.failed === 0 && (
                        <div className="flex items-center gap-2 text-xs text-green-600 mt-2">
                          <Icon name="check-circle" className="w-4 h-4" />
                          OTA配信成功
                        </div>
                      )}

                      {selectedPipeline.ota?.status === 'TRIGGER_FAILED' && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2 text-xs text-red-600">
                            <Icon name="wifi-off" className="w-4 h-4" />
                            デバイス未接続のためトリガー失敗
                          </div>
                          {selectedPipeline.ota?.error && (
                            <p className="text-xs text-red-500 mt-1">{selectedPipeline.ota.error}</p>
                          )}
                        </div>
                      )}

                      {(selectedPipeline.ota?.status === 'FAILED' || selectedPipeline.ota?.failed > 0) && selectedPipeline.ota?.status !== 'TRIGGER_FAILED' && (
                        <div className="flex items-center gap-2 text-xs text-red-600 mt-2">
                          <Icon name="x-circle" className="w-4 h-4" />
                          OTA配信失敗
                          {selectedPipeline.ota?.failureReason && (
                            <span className="text-red-400">({selectedPipeline.ota.failureReason})</span>
                          )}
                        </div>
                      )}

                      {/* OTAテスト再実行ボタン */}
                      {(selectedPipeline.ota?.status === 'TRIGGER_FAILED' ||
                        selectedPipeline.ota?.status === 'FAILED' ||
                        selectedPipeline.ota?.timedOut) &&
                        selectedPipeline.otaAgentBuild?.status === 'COMPLETED' && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={handleRetryOtaTest}
                            disabled={retryingOta}
                            className="w-full"
                          >
                            {retryingOta ? (
                              <>
                                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                再実行中...
                              </>
                            ) : (
                              <>
                                <Icon name="refresh-cw" className="w-4 h-4 mr-2" />
                                OTAテスト再実行
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-slate-500 mt-2 text-center">
                            デバイスがオンラインであることを確認してから再実行してください
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* 承認 */}
                <Card>
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Icon name="check-square" className="w-4 h-4" /> 承認
                  </h4>

                  <div className="p-3 bg-slate-50 rounded-lg mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">承認ステータス</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${APPROVAL_STATUS[selectedPipeline.approval?.status]?.color || 'bg-slate-100'}`}>
                        {APPROVAL_STATUS[selectedPipeline.approval?.status]?.label || '未承認'}
                      </span>
                    </div>

                    {selectedPipeline.approval?.status === 'APPROVED' && (
                      <div className="text-xs text-slate-600">
                        <p><span className="text-slate-400">承認者:</span> {selectedPipeline.approval.approvedByEmail || selectedPipeline.approval.approvedBy}</p>
                        <p><span className="text-slate-400">日時:</span> {formatDate(selectedPipeline.approval.approvedAt)}</p>
                        {selectedPipeline.approval.comment && (
                          <p className="mt-1 italic">"{selectedPipeline.approval.comment}"</p>
                        )}
                      </div>
                    )}

                    {selectedPipeline.approval?.status === 'REJECTED' && (
                      <div className="text-xs text-red-600">
                        <p><span className="text-red-400">却下者:</span> {selectedPipeline.approval.approvedByEmail || selectedPipeline.approval.approvedBy}</p>
                        <p><span className="text-red-400">日時:</span> {formatDate(selectedPipeline.approval.approvedAt)}</p>
                        {selectedPipeline.approval.comment && (
                          <p className="mt-1 italic">"{selectedPipeline.approval.comment}"</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 承認ボタン */}
                  {selectedPipeline.status === 'PENDING_APPROVAL' && (
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => setShowApprovalModal(true)}
                    >
                      <Icon name="check" className="w-4 h-4" /> 承認・却下
                    </Button>
                  )}

                  {selectedPipeline.status === 'APPROVED' && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                      <Icon name="check-circle" className="w-4 h-4 inline mr-2" />
                      パイプライン承認済み
                    </div>
                  )}
                </Card>
              </div>

              {/* パイプライン履歴タイムライン */}
              <Card>
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Icon name="clock" className="w-4 h-4" /> パイプライン履歴
                </h4>
                <ApprovalTimeline events={timelineEvents} loading={false} />
              </Card>
            </>
          ) : (
            <Card className="text-center py-12">
              <Icon name="git-branch" className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">左のリストからパイプラインを選択してください</p>
              <p className="text-sm text-slate-400 mt-2">または「パイプライン開始」で新規作成</p>
            </Card>
          )}
        </div>
      </div>

      {/* パイプライン開始モーダル */}
      <Modal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        title="パイプライン開始"
      >
        <div className="space-y-4">
          {/* 実行状態チェック中 */}
          {checkingStatus && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-slate-600">実行状態を確認中...</span>
            </div>
          )}

          {/* 実行中のテスト/パイプラインがある場合の警告 */}
          {!checkingStatus && executionStatus && !executionStatus.canStart && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <Icon name="alert-circle" className="w-4 h-4" />
                パイプラインを開始できません
              </div>
              <p className="text-sm text-red-600">{executionStatus.message}</p>

              {/* 実行中パイプライン詳細 */}
              {executionStatus.runningPipelines?.length > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  <p className="font-medium">実行中のパイプライン:</p>
                  {executionStatus.runningPipelines.map((p) => (
                    <p key={p.pipelineId} className="ml-2">
                      • {p.pipelineId} ({p.status}, branch: {p.branch})
                    </p>
                  ))}
                </div>
              )}

              {/* 実行中テスト詳細 */}
              {executionStatus.runningTests?.length > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  <p className="font-medium">実行中のテスト:</p>
                  {executionStatus.runningTests.map((t) => (
                    <p key={t.testId} className="ml-2">
                      • {t.testId} ({t.status}, branch: {t.branch})
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 予約されたテストがある場合の注意 */}
          {!checkingStatus && executionStatus?.scheduledTests?.length > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-700 font-medium mb-2">
                <Icon name="clock" className="w-4 h-4" />
                予約されたテストがあります
              </div>
              <div className="text-xs text-yellow-600">
                {executionStatus.scheduledTests.map((s, idx) => (
                  <p key={idx} className="ml-2">
                    • {s.type === 'daily' ? '毎日' : '予約'}: {s.scheduledTime}
                    {s.branch && ` (branch: ${s.branch})`}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* 開始可能な場合の説明 */}
          {!checkingStatus && executionStatus?.canStart && (
            <p className="text-sm text-slate-600">
              {skipSitl && skipHilsBuild
                ? 'SiTLテスト・HiLSビルドをスキップして、OTAテストのみ実行します。'
                : skipSitl
                ? 'SiTLテストをスキップし、HiLSビルドとOTAテストを実行後に承認待ちステータスになります。'
                : skipHilsBuild
                ? 'SiTLテストを実行後、HiLSビルドをスキップしてota-agentビルドとOTAテストを実行します。'
                : 'SiTLテストとHiLSテストを同時に実行し、両方完了後に承認待ちステータスになります。'
              }
            </p>
          )}

          {/* ブランチ選択 */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              ブランチ
            </label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={!executionStatus?.canStart}
            >
              {branches.map((branch) => (
                <option key={branch} value={branch}>{branch}</option>
              ))}
            </select>
          </div>

          {/* オプション */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={gitClean}
                onChange={(e) => setGitClean(e.target.checked)}
                className="rounded border-slate-300"
                disabled={!executionStatus?.canStart}
              />
              <span className="text-sm text-slate-700">Gitクリーンビルド（リポジトリ再クローン）</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={cleanDocker}
                onChange={(e) => setCleanDocker(e.target.checked)}
                className="rounded border-slate-300"
                disabled={!executionStatus?.canStart}
              />
              <span className="text-sm text-slate-700">Dockerクリーン（キャッシュ削除）</span>
            </label>
            <label
              className="flex items-center gap-2 opacity-50 cursor-not-allowed"
              title="Yoctoビルドキャッシュ（約18GB）を削除して再ビルドします。完了まで1時間以上かかる場合があります。"
            >
              <input
                type="checkbox"
                checked={cleanYoctoCache}
                onChange={(e) => setCleanYoctoCache(e.target.checked)}
                className="rounded border-slate-300"
                disabled={true /* 将来有効化: !executionStatus?.canStart */}
              />
              <span className="text-sm text-slate-500">Yoctoキャッシュ削除（HiLS用）<span className="text-xs ml-1">※準備中</span></span>
            </label>
            <label
              className="flex items-center gap-2"
              title="HiLSビルドをスキップして、ota-agentビルドとOTAテストのみ実行します。"
            >
              <input
                type="checkbox"
                checked={skipHilsBuild}
                onChange={(e) => setSkipHilsBuild(e.target.checked)}
                className="rounded border-slate-300"
                disabled={!executionStatus?.canStart}
              />
              <span className="text-sm text-slate-700">HiLSビルドをスキップ（OTAテストのみ実行）</span>
            </label>
            <label
              className="flex items-center gap-2"
              title="SiTLテストをスキップします。SiTLはローカルランナー（devpc2）で実行されます。"
            >
              <input
                type="checkbox"
                checked={skipSitl}
                onChange={(e) => setSkipSitl(e.target.checked)}
                className="rounded border-slate-300"
                disabled={!executionStatus?.canStart}
              />
              <span className="text-sm text-slate-700">SiTLテストをスキップ</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowStartModal(false)}>
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleStartPipeline}
              disabled={starting || checkingStatus || !executionStatus?.canStart}
            >
              {starting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  開始中...
                </>
              ) : (
                <>
                  <Icon name="play" className="w-4 h-4" />
                  パイプライン開始
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 承認モーダル */}
      <ApprovalModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        build={{
          buildId: selectedPipeline?.pipelineId,
          repository: 'uav-dev-env',
          branch: selectedPipeline?.branch,
          testResults: {
            sitl: {
              status: selectedPipeline?.sitl?.status === 'SKIPPED' ? 'SKIPPED' :
                selectedPipeline?.sitl?.status === 'COMPLETED' && selectedPipeline?.sitl?.exitCode === 0 ? 'PASSED' : 'FAILED',
              totalTests: selectedPipeline?.sitl?.testSummary?.total,
              passed: selectedPipeline?.sitl?.testSummary?.passed,
            },
          },
        }}
        onApprove={handleApprove}
        onReject={handleReject}
        loading={approving}
      />

      {/* 成果物検証詳細モーダル */}
      <Modal
        isOpen={showArtifactCheckModal}
        onClose={() => setShowArtifactCheckModal(false)}
        title="ビルド成果物検証詳細"
        size="lg"
      >
        {selectedArtifactCheck && (
          <div className="space-y-4">
            {/* 概要 */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  selectedArtifactCheck.status === 'PASSED' ? 'bg-green-100 text-green-700' :
                  selectedArtifactCheck.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {selectedArtifactCheck.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">ファイル名:</span>
                  <p className="font-medium font-mono">{selectedArtifactCheck.filename}</p>
                </div>
                <div>
                  <span className="text-slate-500">ファイルサイズ:</span>
                  <p className="font-medium">{selectedArtifactCheck.metadata?.sizeFormatted || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">リポジトリ:</span>
                  <p className="font-medium">{selectedArtifactCheck.repo || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">ブランチ:</span>
                  <p className="font-medium">{selectedArtifactCheck.branch || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">コミットSHA:</span>
                  <p className="font-medium font-mono">{selectedArtifactCheck.sha || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">カテゴリ:</span>
                  <p className="font-medium">{selectedArtifactCheck.category || '-'}</p>
                </div>
              </div>
            </div>

            {/* RAUCメタデータ（.raucbの場合のみ表示） */}
            {selectedArtifactCheck.raucMetadata && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Icon name="package" className="w-4 h-4" />
                  RAUCバンドル情報
                </h4>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {/* バージョン情報（メタデータファイルから） */}
                  {selectedArtifactCheck.raucMetadata.version && (
                    <div>
                      <span className="text-blue-600">バージョン:</span>
                      <p className="font-medium">{selectedArtifactCheck.raucMetadata.version}</p>
                    </div>
                  )}
                  {selectedArtifactCheck.raucMetadata.compatible && (
                    <div>
                      <span className="text-blue-600">Compatible:</span>
                      <p className="font-medium">{selectedArtifactCheck.raucMetadata.compatible}</p>
                    </div>
                  )}
                  {selectedArtifactCheck.raucMetadata.description && (
                    <div className="col-span-2">
                      <span className="text-blue-600">説明:</span>
                      <p className="font-medium">{selectedArtifactCheck.raucMetadata.description}</p>
                    </div>
                  )}

                  {/* Squashfs情報 */}
                  {selectedArtifactCheck.raucMetadata.squashfs && (
                    <>
                      <div>
                        <span className="text-blue-600">Squashfsバージョン:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.version}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">圧縮形式:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.compression}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">ブロックサイズ:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.blockSizeFormatted}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">使用サイズ:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.bytesUsedFormatted}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">inode数:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.inodeCount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-blue-600">ビルド日時:</span>
                        <p className="font-medium">{selectedArtifactCheck.raucMetadata.squashfs.modificationDate ? new Date(selectedArtifactCheck.raucMetadata.squashfs.modificationDate).toLocaleString('ja-JP') : '-'}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* メタデータソース */}
                {selectedArtifactCheck.raucMetadata.metadataSource && (
                  <div className="mt-3 text-xs text-blue-500">
                    メタデータソース: {selectedArtifactCheck.raucMetadata.metadataSource}
                  </div>
                )}
              </div>
            )}

            {/* チェック項目（実装済み） */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">検証項目</h4>
              <div className="space-y-2">
                {(selectedArtifactCheck.checks || []).filter(c => !c.isProposal).map((check, idx) => (
                  <div
                    key={idx}
                    className="p-3 border rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {check.status === 'PASSED' && (
                          <Icon name="check-circle" className="w-5 h-5 text-green-500" />
                        )}
                        {check.status === 'FAILED' && (
                          <Icon name="x-circle" className="w-5 h-5 text-red-500" />
                        )}
                        {check.status === 'SKIPPED' && (
                          <Icon name="minus-circle" className="w-5 h-5 text-slate-400" />
                        )}
                        {check.status === 'WARNING' && (
                          <Icon name="alert-triangle" className="w-5 h-5 text-yellow-500" />
                        )}
                        <span className="font-medium text-slate-700">
                          {check.name === 'naming_convention' && '命名規則'}
                          {check.name === 'file_size' && 'ファイルサイズ'}
                          {check.name === 'magic_bytes' && 'ファイルタイプ（マジックバイト）'}
                          {check.name === 'file_type' && 'ファイルタイプ'}
                          {check.name === 'squashfs_header' && 'Squashfsヘッダー解析'}
                          {check.name === 'rauc_metadata_file' && 'RAUCメタデータファイル'}
                          {!['naming_convention', 'file_size', 'magic_bytes', 'file_type', 'squashfs_header', 'rauc_metadata_file'].includes(check.name) && check.name}
                        </span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        check.status === 'PASSED' ? 'bg-green-100 text-green-700' :
                        check.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                        check.status === 'SKIPPED' ? 'bg-slate-100 text-slate-500' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {check.status}
                      </span>
                    </div>

                    {/* チェック詳細 */}
                    {check.details && (
                      <div className="mt-2 text-xs text-slate-500 font-mono bg-slate-50 p-2 rounded">
                        {check.name === 'naming_convention' && check.details.repo && (
                          <div>repo: {check.details.repo}, branch: {check.details.branch}, sha: {check.details.sha}</div>
                        )}
                        {check.name === 'file_size' && check.details.size && (
                          <div>サイズ: {check.details.size.toLocaleString()} bytes</div>
                        )}
                        {check.name === 'magic_bytes' && check.details.expected && (
                          <div>期待値: {check.details.expected}</div>
                        )}
                        {check.name === 'squashfs_header' && check.details.compression && (
                          <div>圧縮: {check.details.compression}, バージョン: {check.details.version}, ブロックサイズ: {check.details.blockSizeFormatted}</div>
                        )}
                        {check.name === 'rauc_metadata_file' && check.details.source && (
                          <div>ソース: {check.details.source}</div>
                        )}
                        {check.details.error && (
                          <div className="text-red-500">{check.details.error}</div>
                        )}
                        {check.status === 'SKIPPED' && check.details.message && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Icon name="info" className="w-3 h-3" />
                            <span>{check.details.message}</span>
                          </div>
                        )}
                        {check.status !== 'SKIPPED' && check.details.message && (
                          <div>{check.details.message}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 検証項目案（将来実装予定） */}
            {(selectedArtifactCheck.checks || []).some(c => c.isProposal) && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Icon name="info" className="w-4 h-4" />
                  検証項目案（将来実装予定）
                </h4>
                <p className="text-xs text-amber-600 mb-3">
                  以下の項目は準備が整い次第、検証を実行します。詳細は docs/artifact_check_items.md を参照してください。
                </p>
                <div className="space-y-2">
                  {(selectedArtifactCheck.checks || []).filter(c => c.isProposal).map((check, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-white border border-amber-200 rounded-lg"
                      title={check.details?.description || ''}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon name="alert-triangle" className="w-5 h-5 text-amber-500" />
                          <span className="font-medium text-amber-800">
                            {check.name === 'rauc_metadata_file' && '[検証項目案] RAUCメタデータファイル'}
                            {check.name === 'rauc_signature' && '[検証項目案] RAUC署名検証'}
                            {check.name === 'compatible_check' && '[検証項目案] Compatible整合性'}
                            {!['rauc_metadata_file', 'rauc_signature', 'compatible_check'].includes(check.name) && `[検証項目案] ${check.name}`}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          check.status === 'PASSED' ? 'bg-green-100 text-green-700' :
                          check.status === 'WARNING' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {check.status === 'PASSED' ? '検証済み' : '未実装'}
                        </span>
                      </div>

                      {/* 詳細（マウスオーバーでも表示されるが、常に表示） */}
                      {check.details && (
                        <div className="mt-2 text-xs text-amber-700">
                          <p className="mb-1">{check.details.description}</p>
                          {check.details.requires && check.details.requires.length > 0 && (
                            <div className="mt-2">
                              <span className="font-medium">必要な準備:</span>
                              <ul className="list-disc list-inside ml-2 mt-1">
                                {check.details.requires.map((req, reqIdx) => (
                                  <li key={reqIdx}>{req}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* エラーメッセージ */}
            {selectedArtifactCheck.errors && selectedArtifactCheck.errors.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">エラー</h4>
                <ul className="text-sm text-red-600 list-disc list-inside">
                  {selectedArtifactCheck.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* メタ情報 */}
            <div className="text-xs text-slate-400 border-t pt-3">
              <p>チェックID: {selectedArtifactCheck.checkId}</p>
              <p>処理時間: {selectedArtifactCheck.duration}ms</p>
              <p>S3バケット: {selectedArtifactCheck.bucket}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
